import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../../firebase";
import Window, { Footer } from "../../components/Window";
import Icon from "../../components/Icon";

// Where Firebase's email links land (once its email templates point here):
// "reset your password" and "verify your email", in the site's own style.
export default function AuthAction() {
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const code = params.get("oobCode") || "";

  const [email, setEmail] = useState("");
  const [state, setState] = useState<
    "checking" | "form" | "saving" | "done" | "verified" | "bad"
  >("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode === "verifyEmail" && code) {
      applyActionCode(auth, code)
        .then(async () => {
          // refresh the signed-in session so posting unlocks right away
          await auth.currentUser?.reload().catch(() => {});
          await auth.currentUser?.getIdToken(true).catch(() => {});
          setState("verified");
        })
        .catch(() => setState("bad"));
      return;
    }
    if (mode !== "resetPassword" || !code) {
      setState("bad");
      return;
    }
    verifyPasswordResetCode(auth, code)
      .then((address) => {
        setEmail(address);
        setState("form");
      })
      .catch(() => setState("bad"));
  }, [mode, code]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Please use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setState("saving");
    try {
      await confirmPasswordReset(auth, code, password);
      setState("done");
    } catch (err: any) {
      setError(
        err?.code === "auth/weak-password"
          ? "That password is too easy to guess. Try a longer one."
          : "This link has expired or was already used. Request a new one."
      );
      setState("form");
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="mb-5 text-center">
          <span className="logo">
            <Icon name="mail" size={37} tone="pink" pop className="mr-1" />
            PenPal Board
            <span className="logo-sub">the 2SLGBTQIA+ pen pal network</span>
          </span>
        </div>

        <Window title="new_password.exe" tone="blue" minimizable={false}>
          {state === "checking" && <p className="empty">Checking your link...</p>}

          {state === "bad" && (
            <div className="flex flex-col gap-3">
              <h1 className="window-heading">This link doesn't work</h1>
              <p>It may have expired or already been used. You can ask for a fresh one.</p>
              {mode === "verifyEmail" ? (
                <Link to="/dashboard" className="btn">
                  Sign in to resend it
                </Link>
              ) : (
                <Link to="/forgot-password" className="btn">
                  Send a new link
                </Link>
              )}
            </div>
          )}

          {state === "verified" && (
            <div className="flex flex-col gap-3">
              <h1 className="window-heading">Email verified ✿</h1>
              <p>Thank you! You can now post, send requests, and write letters.</p>
              <Link to="/dashboard" className="btn">
                Go to PenPal Board
              </Link>
            </div>
          )}

          {state === "done" && (
            <div className="flex flex-col gap-3">
              <h1 className="window-heading">All set ✿</h1>
              <p>Your password has been changed. You can sign in with it now.</p>
              <Link to="/signin" className="btn">
                Sign in
              </Link>
            </div>
          )}

          {(state === "form" || state === "saving") && (
            <form onSubmit={save} className="flex flex-col gap-4">
              <h1 className="window-heading">Choose a new password</h1>
              <p className="window-sub !mb-0">
                for <strong>{email}</strong>
              </p>
              <label className="field">
                <span className="field-label">New password</span>
                <input
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Type it again</span>
                <input
                  type="password"
                  className="input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>
              {error && <p className="error-text">{error}</p>}
              <button type="submit" className="btn w-full" disabled={state === "saving"}>
                {state === "saving" ? "Saving..." : "Save new password"}
              </button>
            </form>
          )}
        </Window>

        <Footer />
      </div>
    </div>
  );
}
